/* PGAcademicInterest.js
   PG-only Program of Study component.
   - University fetched from GetPopulation
   - Schools fetched dynamically from GetPGSchools (CODE_COLLEGE table, filtered by GraduateProgram config)
   - Majors fetched via GetMajors with pgProgramId from API config, then filtered by selected school
   - Level fetched from GetLevels
   - Entry Term locked to September 2026
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'
import Loading from './Loading'

export default class PGAcademicInterest extends Component {
  constructor (props) {
    super(props)
    this.state = {
      loading: true,
      universityData: [],
      majorsData: [],       // raw list from GetMajors (programId from API config)
      filteredMajors: [],   // filtered by selected school collegeId
      levelData: [],
      entryTermData: [],
      schoolData: [],       // from GetPGSchools

      selectedUniversity: props.selectedUniversity || (props.data && props.data.selectedUniversity) || '',
      selectedProgram: props.pgProgramId || '',   // from API config
      selectedSchool: props.selectedSchool || (props.data && props.data.selectedSchool) || '',
      selectedMajor: props.selectedMajor || (props.data && props.data.selectedMajor) || '',
      selectedLevel: props.selectedLevel || (props.data && props.data.selectedLevel) || '',
      selectedEntryTerm: props.selectedEntryTerm || (props.data && props.data.selectedEntryTerm) || '',
      description: props.description || (props.data && props.data.description) || ''
    }
    this.handleUniversitySelect = this.handleUniversitySelect.bind(this)
    this.fetchSchools = this.fetchSchools.bind(this)
    this.fetchMajors = this.fetchMajors.bind(this)
    this.fetchLevel = this.fetchLevel.bind(this)
    this.publish = this.publish.bind(this)
  }

  componentDidUpdate (prevProps) {
    if (prevProps.pgProgramId !== this.props.pgProgramId && this.props.pgProgramId) {
      this.setState({ selectedProgram: this.props.pgProgramId }, () => this.publish())
    }
  }

  componentDidMount () {
    /* 1. Fetch universities */
    fetch(`${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetPopulation`)
      .then(res => res.json())
      .then(res => {
        this.setState({ universityData: res || [] })
      })
      .catch(() => {})

    /* 2. Fetch entry terms and auto-select the latest available */
    fetch(`${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetEntryTerm`)
      .then(res => res.json())
      .then(res => {
        const entryTermData = (res || []).sort((a, b) => b.Id - a.Id)
        let selectedEntryTerm = this.state.selectedEntryTerm || ''
        if (!selectedEntryTerm && entryTermData.length > 0) {
          // Auto-select the first entry (latest term after descending sort)
          selectedEntryTerm = entryTermData[0].Id
        }
        this.setState({ entryTermData, selectedEntryTerm }, () => this.publish())
      })
      .catch(() => {})

    /* 3. If a university was previously selected, load schools & majors */
    const { selectedUniversity, selectedSchool, description } = this.state
    if (selectedUniversity && parseInt(selectedUniversity) > 0) {
      this.fetchSchools(selectedUniversity).then(() => {
        this.fetchMajors(selectedUniversity, true, selectedSchool || null).then(() => {
          if (description) {
            this.fetchLevel(description, 'East')
          }
        })
      })
    } else {
      this.setState({ loading: false })
    }
  }

  fetchSchools (universityId) {
    return fetch(
      `${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetPGSchools?populationId=${universityId}`
    )
      .then(res => res.json())
      .then(res => {
        this.setState({ schoolData: res || [] })
      })
      .catch(() => {})
  }

  fetchMajors (universityId, isInit, schoolCollegeId) {
    // If school is selected use the school-specific endpoint
    const url = schoolCollegeId
      ? `${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetPGMajorsBySchool?collegeId=${schoolCollegeId}&populationId=${universityId}`
      : `${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetMajors?programId=${this.props.pgProgramId}&populationId=${universityId}&campus=East`

    return fetch(url)
      .then(res => res.json())
      .then(res => {
        const majorsData = res || []
        const newState = { majorsData, filteredMajors: majorsData, loading: false }

        /* On initial load, restore previously selected major */
        if (isInit && this.state.description) {
          const found = majorsData.find(m => m.value === this.state.description)
          if (found) {
            newState.selectedMajor = found.Id
            newState.description = this.state.description
          }
        }

        this.setState(newState, () => {
          if (isInit) this.publish()
        })
      })
      .catch(() => this.setState({ loading: false }))
  }

  handleUniversitySelect (val) {
    this.setState({
      selectedUniversity: val,
      selectedSchool: '',
      selectedMajor: '',
      selectedLevel: '',
      description: '',
      majorsData: [],
      filteredMajors: [],
      levelData: [],
      schoolData: [],
      loading: true
    }, () => {
      this.publish()
      if (val && parseInt(val) > 0) {
        this.fetchSchools(val).then(() => {
          this.setState({ loading: false })
        })
      } else {
        this.setState({ loading: false })
      }
    })
  }

  fetchLevel (val, camp) {
    if (!val) return
    fetch(
      `${process.env.REACT_APP_API_URL}/api/AcademicInterest/GetLevels?programOfStudy=${val}&campus=${camp || 'East'}`
    )
      .then(res => res.json())
      .then(res => {
        this.setState({ levelData: res || [] })
      })
      .catch(() => {})
  }

  publish (override = {}) {
    const s = { ...this.state, ...override }
    this.props.collectState('AI', {
      selectedUniversity: s.selectedUniversity,
      selectedProgram: this.props.pgProgramId || '',
      selectedMajor: s.selectedMajor,
      selectedLevel: s.selectedLevel,
      selectedEntryTerm: s.selectedEntryTerm,
      selectedSchool: s.selectedSchool,
      description: s.description,
      /* Not used in PG but kept for save-payload compatibility */
      selectedSecondUniversity: '',
      selectedSecondProgram: '',
      selectedSecondMajor: '',
      selectedSecondLevel: '',
      secondDescription: '',
      preferredCampus: ''
    })
  }

  render () {
    const {
      loading, universityData, filteredMajors, levelData, entryTermData, schoolData,
      selectedUniversity, selectedSchool, selectedMajor, selectedLevel, selectedEntryTerm
    } = this.state
    const { error } = this.props

    if (loading) return <Loading />

    return (
      <div>
        {/* University Selector */}
        <InputRow
          rowName='University'
          name='select'
          value={selectedUniversity}
          data={universityData}
          required='*'
          errMsg={error.selectedUniversity || ''}
          onChange={(val) => this.handleUniversitySelect(val)}
        />

        {/* School — fetched from CODE_COLLEGE via GetPGSchools, shown after university selected */}
        {selectedUniversity && schoolData.length > 0 && (
          <InputRow
            rowName='School Name'
            name='select'
            value={selectedSchool}
            data={schoolData}
            required='*'
            errMsg={error.selectedSchool || ''}
            onChange={(val) => {
              this.setState({
                selectedSchool: val,
                selectedMajor: '',
                selectedLevel: '',
                description: '',
                filteredMajors: [],
                loading: true
              }, () => {
                this.publish()
                // Fetch majors filtered by this school's CollegeId
                this.fetchMajors(this.state.selectedUniversity, false, val)
              })
            }}
          />
        )}

        {/* Programme of Study — filtered by university AND school */}
        {selectedUniversity && selectedSchool && (
          <InputRow
            rowName='Programme of Study'
            name='select'
            value={selectedMajor}
            data={filteredMajors}
            required='*'
            errMsg={error.selectedMajor || error.selectedProgram || ''}
            onChange={(val) => {
              const found = filteredMajors.find(m => String(m.Id) === String(val))
              const description = found ? found.value : ''
              this.setState({ selectedMajor: val, description, selectedLevel: '' }, () => {
                this.publish()
                this.fetchLevel(description, 'East')
              })
            }}
          />
        )}

        {/* Level */}
        {levelData.length > 0 && (
          <InputRow
            rowName='Level'
            name='select'
            value={selectedLevel}
            data={levelData}
            required='*'
            errMsg={error.selectedLevel || ''}
            onChange={(val) => {
              this.setState({ selectedLevel: val }, () => this.publish())
            }}
          />
        )}

        {/* Entry Term */}
        <InputRow
          rowName='Entry Term'
          name='select'
          value={selectedEntryTerm}
          data={entryTermData}
          required='*'
          errMsg={error.selectedEntryTerm || ''}
          onChange={(val) => {
            this.setState({ selectedEntryTerm: val }, () => this.publish())
          }}
        />

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGAcademicInterest.propTypes = {
  onSave: PropTypes.func.isRequired,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  selectedUniversity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedProgram: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedMajor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedLevel: PropTypes.string,
  selectedEntryTerm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSchool: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
  incompleteApplicationId: PropTypes.number
}

PGAcademicInterest.defaultProps = {
  error: {},
  selectedUniversity: '',
  selectedProgram: 3,
  selectedMajor: '',
  selectedLevel: '',
  selectedEntryTerm: '',
  selectedSchool: '',
  description: ''
}
